import type { AdminUserLifecycleStatus, AdminUserServiceStatus } from '@/api/core/users';

export const serviceStatusLabels: Record<AdminUserServiceStatus, string> = {
  ACTIVE: '이용 중',
  SUSPENDED: '일시정지',
};
export const lifecycleStatusLabels: Record<AdminUserLifecycleStatus, string> = {
  ACTIVE: '정상',
  WITHDRAWING: '탈퇴 진행 중',
};

export const USER_ROLES = [
  { bit: 1, label: '게스트' },
  { bit: 2, label: '회원' },
  { bit: 4, label: '스태프' },
  { bit: 8, label: '관리자' },
] as const;

/** 역할 비트를 운영 용어로 바꾼다. null은 권한 미배정이며 "권한 없음 0"과 구분한다. */
export function userRoleLabels(roleBits: number | null): string[] {
  if (roleBits === null) return ['권한 미배정'];
  const known = USER_ROLES.reduce((mask, { bit }) => mask | bit, 0);
  const labels: string[] = USER_ROLES.filter(({ bit }) => (roleBits & bit) !== 0).map(
    ({ label }) => label,
  );
  const unknown = roleBits & ~known;
  if (unknown > 0) labels.push(`알 수 없는 역할 비트 0x${unknown.toString(16)}`);
  return labels.length ? labels : ['역할 없음'];
}

export const DOMAIN_LABELS: Record<string, string> = {
  inventory: '재고 관리',
  booking: '예약·가격·취소',
  user: '사용자 관리',
  property: '지점 관리',
  order: '주문',
  payment: '결제',
  entitlement: '출입 자격',
};

export const DOMAIN_CAPABILITIES: Record<string, readonly { bit: number; label: string }[]> = {
  inventory: [
    { bit: 1, label: '방·슬롯 관리 대상 선택' },
    { bit: 2, label: '편입 적격성 준비' },
    { bit: 4, label: '검증된 미드텀 변경' },
    { bit: 8, label: '방·슬롯 전체 활성화' },
    { bit: 16, label: '관리 재고 조회' },
  ],
  booking: [
    { bit: 1, label: '슬롯 가격 변경' },
    { bit: 2, label: '예약 목록·상세 조회' },
    { bit: 4, label: '규정된 취소' },
    { bit: 8, label: '수수료 면제 취소' },
    { bit: 16, label: '관리자 환불 대상 지정' },
    { bit: 32, label: '객실 판매 관리' },
    { bit: 64, label: '체크인' },
    { bit: 128, label: '체크아웃' },
  ],
  user: [
    { bit: 1, label: '역할·권한 변경과 감사 조회' },
    { bit: 2, label: '사용자 디렉터리 조회' },
    { bit: 4, label: '사용자 이름·상태 변경' },
  ],
  property: [
    { bit: 1, label: '지점 목록 조회' },
    { bit: 2, label: '지점 설정·출입 변경' },
    { bit: 4, label: '지점 콘텐츠 변경' },
  ],
  order: [{ bit: 1, label: '주문·환불 요청 조회' }],
  payment: [
    { bit: 1, label: '결제·환불 처리 정보 조회' },
    { bit: 2, label: '환불 실행' },
  ],
  entitlement: [
    { bit: 1, label: '저장된 출입 자격·발급·회수 상태 조회' },
    { bit: 2, label: '출입 자격 등록·공간 배정' },
  ],
};

export const DOMAIN_ORDER = Object.keys(DOMAIN_LABELS);

export function capabilityLabels(domain: string, mask: number): string[] {
  const known = DOMAIN_CAPABILITIES[domain] ?? [];
  const labels = known.filter(({ bit }) => (mask & bit) !== 0).map(({ label }) => label);
  const unknown = mask & ~known.reduce((all, { bit }) => all | bit, 0);
  if (unknown) labels.push(`알 수 없는 권한 비트 0x${unknown.toString(16)}`);
  return labels;
}
